import React, { useState, useMemo } from "react";
import { useParams } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useSidebarLayout } from "@/hooks/useSidebarLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { apiRequest } from "@/lib/queryClient";
import { 
  Package, 
  Calendar, 
  Building, 
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Download,
  User,
  XCircle,
  Search,
  Filter,
  ChevronUp,
  ChevronDown,
  Eye,
  Settings,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Award,
  X,
  FileSpreadsheet
} from "lucide-react";

interface TenderReturn {
  id?: string; // Added for compatibility
  submissionId: string;
  packageId: string;
  packageName: string;
  packageNumber: string;
  description?: string;
  submittedAt: string;
  notes?: string;
  status: string;
  recipientCompany: string;
  recipientName: string;
  recipientEmail: string;
  hubName?: string;
  projectName?: string;
  filesUploaded?: number;
  filesCount?: number;
  files?: Array<{
    id: string;
    name: string;
    filename: string;
    size: number;
    mimeType: string;
    uploadedAt: string;
  }>;
  returnReason?: string;
  isApproved?: boolean;
  approvedBy?: string;
  approvedAt?: string;
}

export default function TenderReturns() {
  const { projectId } = useParams<{ projectId: string }>();
  const { getPageHeaderClass } = useSidebarLayout();
  const [selectedReturn, setSelectedReturn] = useState<TenderReturn | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [activeOperation, setActiveOperation] = useState<'awarding' | 'rejecting' | null>(null);
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    package: true,
    company: true,
    submittedBy: true,
    submittedDate: true,
    status: true,
    returnNote: true,
    returnFiles: true,
    hubName: false,
    projectName: false,
    actions: true
  });

  // Sorting state - default to submitted date descending
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: 'submittedDate', direction: 'desc' });
  
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Sorting function
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort data function
  const sortData = (data: TenderReturn[]) => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.key) {
        case 'package':
          aValue = a.packageNumber || '';
          bValue = b.packageNumber || '';
          break;
        case 'company':
          aValue = a.recipientCompany || '';
          bValue = b.recipientCompany || '';
          break;
        case 'submittedBy':
          aValue = a.recipientName || '';
          bValue = b.recipientName || '';
          break;
        case 'submittedDate':
          aValue = new Date(a.submittedAt);
          bValue = new Date(b.submittedAt);
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'returnNote':
          aValue = a.notes || '';
          bValue = b.notes || '';
          break;
        case 'returnFiles':
          aValue = a.filesCount || 0;
          bValue = b.filesCount || 0;
          break;
        case 'hubName':
          aValue = a.hubName || '';
          bValue = b.hubName || '';
          break;
        case 'projectName':
          aValue = a.projectName || '';
          bValue = b.projectName || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const { data: tenderReturns = [], isLoading, error } = useQuery<TenderReturn[]>({
    queryKey: ['/api/tender-returns', projectId],
    queryFn: () => {
      const sessionId = localStorage.getItem('sessionId') || '';
      return apiRequest(`/api/tender-returns${projectId ? `?projectId=${projectId}&sessionId=${sessionId}` : `?sessionId=${sessionId}`}`);
    }
  });

  // Filter tender returns based on search and filter criteria
  const filteredTenderReturns = useMemo(() => {
    if (!Array.isArray(tenderReturns)) return [];
    const filtered = tenderReturns.filter(returnItem => {
      // Exclude "sent" status records
      if (returnItem.status === "sent") {
        return false;
      }

      // Search filter
      const matchesSearch = searchQuery === "" || 
        returnItem.packageNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        returnItem.packageName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        returnItem.recipientCompany.toLowerCase().includes(searchQuery.toLowerCase()) ||
        returnItem.recipientName.toLowerCase().includes(searchQuery.toLowerCase());

      // Status filter
      const matchesStatus = statusFilter === "all" || returnItem.status === statusFilter;

      // Company filter
      const matchesCompany = companyFilter === "all" || returnItem.recipientCompany === companyFilter;

      return matchesSearch && matchesStatus && matchesCompany;
    });

    // Apply sorting
    return sortData(filtered);
  }, [tenderReturns, searchQuery, statusFilter, companyFilter, sortConfig]);

  // Get unique companies for filter dropdown
  const uniqueCompanies = useMemo(() => {
    if (!Array.isArray(tenderReturns)) return [];
    return [...new Set(tenderReturns.map(ret => ret.recipientCompany))].sort();
  }, [tenderReturns]);

  // Get unique statuses for filter dropdown based on actual data
  const uniqueStatuses = useMemo(() => {
    if (!Array.isArray(tenderReturns)) return [];
    const statuses = [...new Set(tenderReturns.map(ret => ret.status).filter(Boolean))];
    return statuses.sort();
  }, [tenderReturns]);

  // Map status value to display text
  const getStatusDisplayText = (status: string): string => {
    switch (status) {
      case 'tender_returned':
        return 'Not Reviewed';
      case 'submitted':
        return 'Submitted';
      case 'under_review':
        return 'Under Review';
      case 'awarded':
        return 'Awarded';
      case 'rejected':
        return 'Not Awarded';
      case 'pending':
        return 'Pending';
      default:
        return status || 'Unknown';
    }
  };

  // Status update mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ submissionId, status }: { submissionId: string; status: string }) => {
      // Set active operation based on status
      if (status === 'awarded') {
        setActiveOperation('awarding');
      } else if (status === 'rejected') {
        setActiveOperation('rejecting');
      }
      
      const response = await fetch(`/api/tender-returns/${submissionId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update status');
      }
      
      return response.json();
    },
    onSuccess: (data, variables) => {
      // Clear active operation
      setActiveOperation(null);
      
      // Close the popup immediately
      setShowDetailDialog(false);
      
      // Update local state
      if (selectedReturn && selectedReturn.id === variables.submissionId) {
        setSelectedReturn({ ...selectedReturn, status: variables.status });
      }
      
      // Refresh the data
      queryClient.invalidateQueries({ queryKey: ['/api/tender-returns'] });
      
      // Show success message
      const statusText = variables.status === 'awarded' ? 'awarded' : 
                        variables.status === 'rejected' ? 'rejected' : 'reset';
      toast({
        title: "Status Updated",
        description: `Tender return has been ${statusText} successfully.`,
      });

      // If awarded, reject all other submissions for the same package
      if (variables.status === 'awarded' && Array.isArray(tenderReturns)) {
        const otherSubmissions = tenderReturns.filter(
          r => r.packageId === selectedReturn?.packageId && r.id !== variables.submissionId
        );
        
        // Use a separate mutation for auto-rejecting to avoid UI conflicts
        otherSubmissions.forEach(submission => {
          fetch(`/api/tender-returns/${submission.id}/status`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'rejected' }),
          }).then(response => {
            if (!response.ok) {
              console.error(`Failed to auto-reject submission ${submission.id}`);
            }
          }).catch(error => {
            console.error(`Failed to auto-reject submission ${submission.id}:`, error);
          });
        });
      }
    },
    onError: () => {
      setActiveOperation(null);
      toast({
        title: "Error",
        description: "Failed to update tender status. Please try again.",
        variant: "destructive",
      });
    },
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const updateReturnStatus = (tenderReturnId: string, status: string) => {
    // Prevent multiple operations at the same time
    if (activeOperation !== null) {
      return;
    }
    
    updateStatusMutation.mutate({ submissionId: tenderReturnId, status });
  };

  // Export CSV function
  const exportToCSV = () => {
    setIsExporting(true);
    
    try {
      // Prepare CSV headers based on visible columns
      const headers = [];
      if (visibleColumns.package) headers.push('Package Number', 'Package Name');
      if (visibleColumns.company) headers.push('Company');
      if (visibleColumns.submittedBy) headers.push('Submitted By', 'Email');
      if (visibleColumns.submittedDate) headers.push('Submitted Date');
      if (visibleColumns.status) headers.push('Status');
      if (visibleColumns.returnNote) headers.push('Return Note');
      if (visibleColumns.returnFiles) headers.push('Return Files Count');
      if (visibleColumns.hubName) headers.push('Hub Name');
      if (visibleColumns.projectName) headers.push('Project Name');

      // Convert data to CSV format
      const csvContent = [
        headers.join(','),
        ...filteredTenderReturns.map(returnItem => {
          const row = [];
          if (visibleColumns.package) {
            row.push(`"${returnItem.packageNumber || ''}"`, `"${returnItem.packageName || ''}"`);
          }
          if (visibleColumns.company) {
            row.push(`"${returnItem.recipientCompany || ''}"`);
          }
          if (visibleColumns.submittedBy) {
            row.push(`"${returnItem.recipientName || ''}"`, `"${returnItem.recipientEmail || ''}"`);
          }
          if (visibleColumns.submittedDate) {
            row.push(`"${new Date(returnItem.submittedAt).toLocaleString()}"`);
          }
          if (visibleColumns.status) {
            const statusText = returnItem.status === 'awarded' ? 'Awarded' : 
                             returnItem.status === 'rejected' ? 'Not Awarded' : 
                             returnItem.status === 'tender_returned' || returnItem.status === 'submitted' ? 'Not Reviewed' : 'Not Awarded';
            row.push(`"${statusText}"`);
          }
          if (visibleColumns.returnNote) {
            row.push(`"${returnItem.notes || 'N/A'}"`);
          }
          if (visibleColumns.returnFiles) {
            row.push(`"${returnItem.filesCount || 0}"`);
          }
          if (visibleColumns.hubName) {
            row.push(`"${returnItem.hubName || 'N/A'}"`);
          }
          if (visibleColumns.projectName) {
            row.push(`"${returnItem.projectName || 'N/A'}"`);
          }
          return row.join(',');
        })
      ].join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `tender_returns_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Export Successful",
        description: `Exported ${filteredTenderReturns.length} tender returns to CSV.`,
      });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export data to CSV. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'tender_returned':
      case 'submitted':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'; // Not Reviewed - Bright Yellow
      case 'awarded':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200'; // Awarded - Green
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200'; // Rejected - Red
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'; // Default to Not Reviewed
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'tender_returned':
      case 'submitted':
        return 'Not Reviewed';
      case 'awarded':
        return 'Awarded';
      case 'rejected':
        return 'Not Awarded';
      default:
        return 'Not Awarded';
    }
  };

  return (
    <div className="h-full flex flex-col relative">
      {/* Global Loading Overlay */}
      {updateStatusMutation.isPending && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-700 font-medium">Updating status...</span>
          </div>
        </div>
      )}

      {/* Fixed Header */}
      {/* Fixed Page Header - single line */}
      <div className={getPageHeaderClass()}>
        <div className="h-full flex items-center px-6">
          <h1 className="text-2xl font-bold">Tender Returns</h1>
        </div>
      </div>

      {/* Main Content */}
      {/* Main Content with top padding to account for both headers (64px global + 64px page = 128px = pt-32) */}
      <main className="flex-1 pt-32 p-6">
        {/* Summary Cards with reduced margin */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-4 mt-2">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Returns</p>
                  <p className="text-2xl font-bold text-gray-900">{filteredTenderReturns.length}</p>
                </div>
                <Package className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Submitted Today</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {filteredTenderReturns.filter(ret => {
                      const today = new Date().toDateString();
                      return new Date(ret.submittedAt).toDateString() === today;
                    }).length}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Companies</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {new Set(filteredTenderReturns.map(ret => ret.recipientCompany)).size}
                  </p>
                </div>
                <Building className="w-8 h-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">This Week</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {filteredTenderReturns.filter(ret => {
                      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                      return new Date(ret.submittedAt) >= weekAgo;
                    }).length}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Returns Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Submitted Tender Returns ({filteredTenderReturns.length})
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportToCSV}
                  disabled={isExporting || filteredTenderReturns.length === 0}
                  className="h-8"
                >
                  {isExporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Export CSV
                    </>
                  )}
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8">
                      <Settings className="w-4 h-4 mr-2" />
                      Columns
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64" align="end">
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Toggle columns</h4>
                      <div className="space-y-2">
                        {Object.entries(visibleColumns).map(([key, visible]) => (
                          <div key={key} className="flex items-center space-x-2">
                            <Checkbox
                              id={key}
                              checked={visible}
                              onCheckedChange={(checked) =>
                                setVisibleColumns(prev => ({
                                  ...prev,
                                  [key]: checked
                                }))
                              }
                            />
                            <label
                              htmlFor={key}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {key === 'package' ? 'Package' :
                               key === 'company' ? 'Company' :
                               key === 'submittedBy' ? 'Submitted By' :
                               key === 'submittedDate' ? 'Submitted Date' :
                               key === 'status' ? 'Status' :
                               key === 'returnNote' ? 'Return Note' :
                               key === 'returnFiles' ? 'Return Files' :
                               key === 'hubName' ? 'Hub Name' :
                               key === 'projectName' ? 'Project Name' :
                               key === 'actions' ? 'Actions' : String(key)}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  className="text-gray-600 hover:text-gray-900 h-8"
                >
                  {filtersExpanded ? (
                    <>
                      <ChevronUp className="w-3 h-3 mr-1" />
                      Hide Filters
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3 h-3 mr-1" />
                      Show Filters
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          {filtersExpanded && (
            <CardContent className="pt-0 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">Search</label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                    <Input
                      placeholder="Search by package, company, or person..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8 h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">Status</label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {uniqueStatuses.map(status => (
                        <SelectItem key={status} value={status}>
                          {getStatusDisplayText(status)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">Company</label>
                  <Select value={companyFilter} onValueChange={setCompanyFilter}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="All Companies" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Companies</SelectItem>
                      {uniqueCompanies.map(company => (
                        <SelectItem key={company} value={company}>{company}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          )}
          <CardContent className={filtersExpanded ? "pt-0" : ""}>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading tender returns...</p>
              </div>
            ) : error ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Failed to load tender returns. Please try refreshing the page.
                </AlertDescription>
              </Alert>
            ) : filteredTenderReturns.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>{Array.isArray(tenderReturns) && tenderReturns.length === 0 ? "No tender returns submitted yet." : "No tender returns match the current filters."}</p>
                {Array.isArray(tenderReturns) && tenderReturns.length > 0 && filteredTenderReturns.length === 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      setSearchQuery("");
                      setStatusFilter("all");
                      setCompanyFilter("all");
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : (
              <div className="w-full">
                
                <div className="overflow-x-auto">
                  <div className="min-w-full">
                    <Table>
                    <TableHeader>
                      <TableRow>
                        {visibleColumns.package && (
                          <TableHead 
                            className={`min-w-[120px] cursor-pointer hover:bg-gray-50 select-none ${
                              sortConfig?.key === 'package' ? 'bg-blue-50 border-b-2 border-blue-500' : ''
                            }`}
                            onClick={() => handleSort('package')}
                          >
                            <div className="flex items-center gap-2">
                              <span className={sortConfig?.key === 'package' ? 'font-semibold text-blue-700' : ''}>
                                Package
                              </span>
                              {sortConfig?.key === 'package' ? (
                                sortConfig.direction === 'asc' ? (
                                  <ArrowUp className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <ArrowDown className="w-4 h-4 text-blue-600" />
                                )
                              ) : (
                                <ArrowUpDown className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          </TableHead>
                        )}
                        {visibleColumns.company && (
                          <TableHead 
                            className={`min-w-[150px] cursor-pointer hover:bg-gray-50 select-none ${
                              sortConfig?.key === 'company' ? 'bg-blue-50 border-b-2 border-blue-500' : ''
                            }`}
                            onClick={() => handleSort('company')}
                          >
                            <div className="flex items-center gap-2">
                              <span className={sortConfig?.key === 'company' ? 'font-semibold text-blue-700' : ''}>
                                Company
                              </span>
                              {sortConfig?.key === 'company' ? (
                                sortConfig.direction === 'asc' ? (
                                  <ArrowUp className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <ArrowDown className="w-4 h-4 text-blue-600" />
                                )
                              ) : (
                                <ArrowUpDown className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          </TableHead>
                        )}
                        {visibleColumns.submittedBy && (
                          <TableHead 
                            className={`min-w-[120px] cursor-pointer hover:bg-gray-50 select-none ${
                              sortConfig?.key === 'submittedBy' ? 'bg-blue-50 border-b-2 border-blue-500' : ''
                            }`}
                            onClick={() => handleSort('submittedBy')}
                          >
                            <div className="flex items-center gap-2">
                              <span className={sortConfig?.key === 'submittedBy' ? 'font-semibold text-blue-700' : ''}>
                                Submitted By
                              </span>
                              {sortConfig?.key === 'submittedBy' ? (
                                sortConfig.direction === 'asc' ? (
                                  <ArrowUp className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <ArrowDown className="w-4 h-4 text-blue-600" />
                                )
                              ) : (
                                <ArrowUpDown className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          </TableHead>
                        )}
                        {visibleColumns.submittedDate && (
                          <TableHead 
                            className={`min-w-[100px] text-center cursor-pointer hover:bg-gray-50 select-none ${
                              sortConfig?.key === 'submittedDate' ? 'bg-blue-50 border-b-2 border-blue-500' : ''
                            }`}
                            onClick={() => handleSort('submittedDate')}
                          >
                            <div className="flex items-center justify-center gap-2">
                              <span className={sortConfig?.key === 'submittedDate' ? 'font-semibold text-blue-700' : ''}>
                                Submitted Date
                              </span>
                              {sortConfig?.key === 'submittedDate' ? (
                                sortConfig.direction === 'asc' ? (
                                  <ArrowUp className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <ArrowDown className="w-4 h-4 text-blue-600" />
                                )
                              ) : (
                                <ArrowUpDown className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          </TableHead>
                        )}
                        {visibleColumns.status && (
                          <TableHead 
                            className={`min-w-[80px] text-center cursor-pointer hover:bg-gray-50 select-none ${
                              sortConfig?.key === 'status' ? 'bg-blue-50 border-b-2 border-blue-500' : ''
                            }`}
                            onClick={() => handleSort('status')}
                          >
                            <div className="flex items-center justify-center gap-2">
                              <span className={sortConfig?.key === 'status' ? 'font-semibold text-blue-700' : ''}>
                                Status
                              </span>
                              {sortConfig?.key === 'status' ? (
                                sortConfig.direction === 'asc' ? (
                                  <ArrowUp className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <ArrowDown className="w-4 h-4 text-blue-600" />
                                )
                              ) : (
                                <ArrowUpDown className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          </TableHead>
                        )}
                        {visibleColumns.returnNote && (
                          <TableHead 
                            className={`min-w-[120px] cursor-pointer hover:bg-gray-50 select-none ${
                              sortConfig?.key === 'returnNote' ? 'bg-blue-50 border-b-2 border-blue-500' : ''
                            }`}
                            onClick={() => handleSort('returnNote')}
                          >
                            <div className="flex items-center gap-2">
                              <span className={sortConfig?.key === 'returnNote' ? 'font-semibold text-blue-700' : ''}>
                                Return Note
                              </span>
                              {sortConfig?.key === 'returnNote' ? (
                                sortConfig.direction === 'asc' ? (
                                  <ArrowUp className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <ArrowDown className="w-4 h-4 text-blue-600" />
                                )
                              ) : (
                                <ArrowUpDown className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          </TableHead>
                        )}
                        {visibleColumns.returnFiles && (
                          <TableHead 
                            className={`min-w-[80px] text-center cursor-pointer hover:bg-gray-50 select-none ${
                              sortConfig?.key === 'returnFiles' ? 'bg-blue-50 border-b-2 border-blue-500' : ''
                            }`}
                            onClick={() => handleSort('returnFiles')}
                          >
                            <div className="flex items-center justify-center gap-2">
                              <span className={sortConfig?.key === 'returnFiles' ? 'font-semibold text-blue-700' : ''}>
                                Return Files
                              </span>
                              {sortConfig?.key === 'returnFiles' ? (
                                sortConfig.direction === 'asc' ? (
                                  <ArrowUp className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <ArrowDown className="w-4 h-4 text-blue-600" />
                                )
                              ) : (
                                <ArrowUpDown className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          </TableHead>
                        )}
                        {visibleColumns.hubName && (
                          <TableHead 
                            className="min-w-[100px] cursor-pointer hover:bg-gray-50 select-none"
                            onClick={() => handleSort('hubName')}
                          >
                            <div className="flex items-center gap-2">
                              Hub Name
                              {sortConfig?.key === 'hubName' ? (
                                sortConfig.direction === 'asc' ? (
                                  <ArrowUp className="w-4 h-4" />
                                ) : (
                                  <ArrowDown className="w-4 h-4" />
                                )
                              ) : (
                                <ArrowUpDown className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          </TableHead>
                        )}
                        {visibleColumns.projectName && (
                          <TableHead 
                            className="min-w-[200px] cursor-pointer hover:bg-gray-50 select-none"
                            onClick={() => handleSort('projectName')}
                          >
                            <div className="flex items-center gap-2">
                              Project Name
                              {sortConfig?.key === 'projectName' ? (
                                sortConfig.direction === 'asc' ? (
                                  <ArrowUp className="w-4 h-4" />
                                ) : (
                                  <ArrowDown className="w-4 h-4" />
                                )
                              ) : (
                                <ArrowUpDown className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          </TableHead>
                        )}
                        {visibleColumns.actions && <TableHead className="min-w-[120px] text-center">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTenderReturns.map((returnItem: TenderReturn, index: number) => (
                        <TableRow 
                          key={`${returnItem.submissionId}-${returnItem.packageId}-${index}`} 
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => {
                            setSelectedReturn(returnItem);
                            setShowDetailDialog(true);
                          }}
                        >
                          {/* Package */}
                          {visibleColumns.package && (
                            <TableCell className="font-medium">
                              <div>
                                <div className="font-mono text-sm text-blue-600 font-bold">
                                  {returnItem.packageNumber}
                                </div>
                                <div className="font-medium text-gray-900 text-sm">
                                  {returnItem.packageName}
                                </div>
                              </div>
                            </TableCell>
                          )}
                          
                          {/* Company */}
                          {visibleColumns.company && (
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Building className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                <span className="font-medium text-gray-900 truncate">
                                  {returnItem.recipientCompany || 'No Company'}
                                </span>
                              </div>
                            </TableCell>
                          )}
                          
                          {/* Submitted By */}
                          {visibleColumns.submittedBy && (
                            <TableCell>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex items-center gap-2">
                                      <User className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                      <div className="min-w-0 flex-1">
                                        <div className="font-medium text-gray-900 text-sm truncate">
                                          {returnItem.recipientName || 'No Name'}
                                        </div>
                                        <div className="text-xs text-gray-500 truncate max-w-[100px]">
                                          {returnItem.recipientEmail && returnItem.recipientEmail.length > 15 
                                            ? `${returnItem.recipientEmail.substring(0, 15)}...` 
                                            : returnItem.recipientEmail || 'No Email'}
                                        </div>
                                      </div>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-sm">
                                      <div className="font-medium">{returnItem.recipientName}</div>
                                      <div className="text-gray-400">{returnItem.recipientEmail}</div>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          )}
                          
                          {/* Submitted Date */}
                          {visibleColumns.submittedDate && (
                            <TableCell className="text-center">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="text-sm text-gray-600">
                                      <div className="font-medium">
                                        {new Date(returnItem.submittedAt).toLocaleDateString()}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {new Date(returnItem.submittedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                      </div>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-sm">
                                      {new Date(returnItem.submittedAt).toLocaleString()}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          )}
                          
                          {/* Status */}
                          {visibleColumns.status && (
                            <TableCell className="text-center">
                              <Button
                                variant="outline"
                                size="sm"
                                className={`h-8 px-3 text-xs cursor-pointer transition-all hover:scale-105 ${
                                  returnItem.status === 'awarded' 
                                    ? 'border-green-500 text-green-600 hover:bg-green-50' 
                                    : returnItem.status === 'rejected' 
                                    ? 'border-red-500 text-red-600 hover:bg-red-50'
                                    : (returnItem.status === 'submitted' || returnItem.status === 'tender_returned')
                                    ? 'border-blue-500 text-blue-600 hover:bg-blue-50'
                                    : 'border-gray-400 text-gray-600 hover:bg-gray-50'
                                }`}
                                onClick={() => {
                                  setSelectedReturn(returnItem);
                                  setShowDetailDialog(true);
                                }}
                              >
                                {returnItem.status === 'awarded' ? (
                                  <>
                                    <Award className="w-3 h-3 mr-1" />
                                    Awarded
                                  </>
                                ) : returnItem.status === 'rejected' ? (
                                  <>
                                    <XCircle className="w-3 h-3 mr-1" />
                                    Not Awarded
                                  </>
                                ) : returnItem.status === 'submitted' ? (
                                  <>
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Submitted
                                  </>
                                ) : returnItem.status === 'tender_returned' ? (
                                  <>
                                    <Clock className="w-3 h-3 mr-1" />
                                    Not Reviewed
                                  </>
                                ) : (
                                  <>
                                    <Clock className="w-3 h-3 mr-1" />
                                    Not Reviewed
                                  </>
                                )}
                              </Button>
                            </TableCell>
                          )}
                          
                          {/* Return Note */}
                          {visibleColumns.returnNote && (
                            <TableCell>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-sm text-gray-700 truncate block">
                                      {returnItem.notes || 'N/A'}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-sm max-w-xs">
                                      {returnItem.notes || 'No notes provided'}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          )}
                          
                          {/* Return Files */}
                          {visibleColumns.returnFiles && (
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <FileText className="w-4 h-4 text-gray-500" />
                                <span className="text-sm font-medium">
                                  {returnItem.filesCount || 0} ({returnItem.files?.length || 0})
                                </span>
                              </div>
                            </TableCell>
                          )}
                          
                          {/* Hub Name */}
                          {visibleColumns.hubName && (
                            <TableCell>
                              <span className="text-sm text-gray-700 truncate block">
                                {returnItem.hubName || 'N/A'}
                              </span>
                            </TableCell>
                          )}
                          
                          {/* Project Name */}
                          {visibleColumns.projectName && (
                            <TableCell>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-sm text-gray-700 truncate block">
                                      {returnItem.projectName || 'N/A'}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-sm max-w-xs">
                                      {returnItem.projectName || 'No project name'}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          )}
                          
                          {/* Actions */}
                          {visibleColumns.actions && (
                            <TableCell className="text-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedReturn(returnItem);
                                  setShowDetailDialog(true);
                                }}
                                className="h-8 px-3 bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-700 text-xs"
                                disabled={updateStatusMutation.isPending}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                View Details
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Return Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto bg-white border-gray-200 shadow-xl text-gray-900">
          <DialogHeader className="relative">
            <div className="flex items-start justify-between">
              <div className="flex-1 pr-8">
                <DialogTitle className="text-xl font-semibold text-gray-900">
                  Tender Return Details - {selectedReturn?.packageName}
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Complete details of tender return submission for {selectedReturn?.packageNumber}
                </DialogDescription>
              </div>
              {/* Status Badge - Dynamic styling based on status */}
              <div className="flex-shrink-0 mr-16">
                <Badge className={`
                  font-medium text-sm px-4 py-2 rounded-full
                  shadow-sm border
                  flex items-center gap-2
                  transition-all duration-200 hover:shadow-md
                  ${selectedReturn?.status === 'awarded' 
                    ? 'bg-emerald-100 text-emerald-800 border-emerald-200' 
                    : selectedReturn?.status === 'tender_returned' || selectedReturn?.status === 'submitted'
                    ? 'bg-blue-100 text-blue-800 border-blue-200'
                    : selectedReturn?.status === 'rejected'
                    ? 'bg-red-100 text-red-800 border-red-200'
                    : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                  }
                `}>
                  {selectedReturn?.status === 'awarded' ? (
                    <Award className="w-4 h-4" />
                  ) : (selectedReturn?.status === 'tender_returned' || selectedReturn?.status === 'submitted') ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : selectedReturn?.status === 'rejected' ? (
                    <X className="w-4 h-4" />
                  ) : (
                    <Clock className="w-4 h-4" />
                  )}
                  {getStatusText(selectedReturn?.status || '')}
                </Badge>
              </div>
            </div>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Return Information */}
            <div className="bg-gray-50 border border-gray-300 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Submission Information</h3>
              
              {/* Row 1: Package Number | Package Name */}
              <div className="grid grid-cols-2 gap-6 mb-4">
                <div>
                  <span className="font-medium text-gray-900">Package Number:</span>
                  <span className="ml-2 text-gray-800 font-mono">{selectedReturn?.packageNumber}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-900">Package Name:</span>
                  <span className="ml-2 text-gray-800">{selectedReturn?.packageName}</span>
                </div>
              </div>

              {/* Row 2: Tender Company | Submitted By */}
              <div className="grid grid-cols-2 gap-6 mb-4">
                <div>
                  <span className="font-medium text-gray-900">Tender Company:</span>
                  <span className="ml-2 text-gray-800">{selectedReturn?.recipientCompany}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-900">Submitted By:</span>
                  <span className="ml-2 text-gray-800">{selectedReturn?.recipientName}</span>
                </div>
              </div>

              {/* Row 3: Email | Submitted Date */}
              <div className="grid grid-cols-2 gap-6 mb-4">
                <div>
                  <span className="font-medium text-gray-900">Email:</span>
                  <span className="ml-2 text-gray-800">{selectedReturn?.recipientEmail}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-900">Submitted Date:</span>
                  <span className="ml-2 text-gray-800">
                    {selectedReturn?.submittedAt ? formatDate(selectedReturn.submittedAt) : 'N/A'}
                  </span>
                </div>
              </div>

              {/* Row 4: Submission Notes (full width) */}
              <div className="mt-4">
                <span className="font-medium text-gray-900">Submission Notes:</span>
                <div className="mt-1 text-gray-800 bg-white p-3 rounded border border-gray-300">
                  {selectedReturn?.notes || 'No notes provided'}
                </div>
              </div>
              
              {selectedReturn?.description && (
                <div className="mt-4">
                  <span className="font-medium text-gray-900">Package Description:</span>
                  <div className="mt-1 text-gray-800 bg-white p-3 rounded border border-gray-300">
                    {selectedReturn.description}
                  </div>
                </div>
              )}
              
            </div>

            {/* Files and Actions */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white border-2 border-gray-300 rounded-lg p-6 shadow-sm">
                <h4 className="font-bold text-gray-900 mb-4 text-lg">Files Uploaded</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 mb-3">
                    <FileText className="w-6 h-6 text-blue-600" />
                    <span className="text-lg font-bold text-gray-900">
                      {selectedReturn?.files && selectedReturn.files.length > 0 ? selectedReturn.files.length : 0} files
                    </span>
                  </div>
                  
                  {/* File List */}
                  {selectedReturn?.files && selectedReturn.files.length > 0 ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedReturn.files.map((file: any, index: number) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            <span className="text-sm text-gray-800 truncate" title={file.name || file.filename || `File ${index + 1}`}>
                              {file.name || file.filename || `File ${index + 1}`}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 ml-2">
                            {file.size ? `${(file.size / 1024).toFixed(1)} KB` : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-500 text-sm italic">No files uploaded</div>
                  )}
                </div>
              </div>

              <div className="bg-white border-2 border-gray-300 rounded-lg p-6 shadow-sm">
                <h4 className="font-bold text-gray-900 mb-4 text-lg">Actions</h4>
                <div className="space-y-3">
                  {/* Download Button */}
                  <Button
                    onClick={async () => {
                      if (!selectedReturn?.id) return;
                      
                      // Check if there are files to download
                      const filesCount = selectedReturn.filesUploaded || selectedReturn.filesCount || 0;
                      if (filesCount === 0) {
                        toast({
                          title: "No Files Available",
                          description: "This tender return has no files to download.",
                          variant: "destructive",
                        });
                        return;
                      }
                      
                      setIsDownloading(true);
                      try {
                        const response = await fetch(`/api/tender-return-files/download-all/${selectedReturn.id}?email=${encodeURIComponent(selectedReturn.recipientEmail || '')}`, {
                          method: 'GET',
                        });
                        
                        if (response.ok) {
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.style.display = 'none';
                          a.href = url;
                          a.download = `${selectedReturn.packageNumber}_Tender_Return_Files.zip`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } else if (response.status === 404) {
                          toast({
                            title: "Files Not Found",
                            description: "No files are available for this tender return.",
                            variant: "destructive",
                          });
                        } else {
                          toast({
                            title: "Download Failed",
                            description: `Failed to download files: ${response.statusText}`,
                            variant: "destructive",
                          });
                        }
                      } catch (error) {
                        console.error('Error downloading files:', error);
                        toast({
                          title: "Download Error",
                          description: "An error occurred while downloading files.",
                          variant: "destructive",
                        });
                      } finally {
                        setIsDownloading(false);
                      }
                    }}
                    variant="outline"
                    className="w-full border-blue-500 text-blue-600 hover:bg-blue-50 font-semibold"
                    disabled={isDownloading || !selectedReturn?.filesUploaded || selectedReturn.filesUploaded === 0}
                  >
                    {isDownloading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download Files
                      </>
                    )}
                  </Button>

                  {/* Status Management Buttons */}
                  {selectedReturn?.status !== 'awarded' && selectedReturn?.status !== 'rejected' && (
                    <>
                      <Button
                        onClick={() => updateReturnStatus(selectedReturn?.id || '', 'awarded')}
                        className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold"
                        disabled={activeOperation !== null}
                      >
                        {activeOperation === 'awarding' ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Awarding...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Award Tender
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => updateReturnStatus(selectedReturn?.id || '', 'rejected')}
                        variant="outline"
                        className="w-full border-red-500 text-red-600 hover:bg-red-50 font-semibold"
                        disabled={activeOperation !== null}
                      >
                        {activeOperation === 'rejecting' ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                            Rejecting...
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject Tender
                          </>
                        )}
                      </Button>
                    </>
                  )}
                  {(selectedReturn?.status === 'awarded' || selectedReturn?.status === 'rejected') && (
                    <Button
                      onClick={() => updateReturnStatus(selectedReturn?.id || '', 'tender_returned')}
                      variant="outline"
                      className="w-full border-gray-500 text-gray-600 hover:bg-gray-50 font-semibold"
                      disabled={updateStatusMutation.isPending}
                    >
                      {updateStatusMutation.isPending ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                          Resetting...
                        </>
                      ) : (
                        'Reset Status'
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>

          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}